<script setup lang="ts">
import type { ItemWithKey } from "@/interfaces";
import {
  useSortable,
  moveArrayElement,
} from "@vueuse/integrations/useSortable";
import { nextTick, shallowRef, toRefs, useTemplateRef, watch } from "vue";

interface Props {
  items: ItemWithKey[];
  defaultGap?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  defaultGap: "gap-3",
});

const emit = defineEmits<{ update: [string[]] }>();

const { items, disabled } = toRefs(props);

const listContainerRef = useTemplateRef<HTMLElement>("listContainerRef");
const list = shallowRef(items.value);

// Keep list in sync with items prop
watch(
  items,
  (newItems) => {
    list.value = [...newItems];
  },
  { deep: true },
);

const sortableInstance = useSortable(listContainerRef, list, {
  animation: 300,
  disabled: disabled.value,
  onUpdate: (e) => {
    if (e.oldIndex === undefined || e.newIndex === undefined) return;
    // Update the reactive list to match the new DOM order
    moveArrayElement(list.value, e.oldIndex, e.newIndex);
    nextTick(() => {
      emit(
        "update",
        list.value.map((item) => item.key),
      );
    });
  },
  onStart(evt) {
    evt.item.classList.add("dragging");
  },

  onEnd(evt) {
    evt.item.classList.remove("dragging");
  },
});

// Make disabled reactive
watch(disabled, (isDisabled) => {
  if (sortableInstance) {
    sortableInstance.option("disabled", isDisabled);
  }
});
</script>

<template>
  <div class="flex flex-col" :class="defaultGap" ref="listContainerRef">
    <div v-for="(item, index) of list" :key="item.key">
      <slot :item="item" :index="index"></slot>
    </div>
  </div>
</template>
